import React, { useEffect, useRef } from 'react';
import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Image from '@tiptap/extension-image'
import YouTube from '@tiptap/extension-youtube'
import TextStyle from '@tiptap/extension-text-style'
import FontFamily from '@tiptap/extension-font-family'
import { Color } from '@tiptap/extension-color'
import Bold from '@tiptap/extension-bold'

const WysiwygEditor = ({ onChange, minHeight = '150px', maxHeight = '300px', minWidth = '580px', maxWidth = '580px', HTMLInput = '' }) => {
  const editorContainerRef = useRef(null);
  const editorRef = useRef(null); // Store the editor instance so we can reference it easily

  useEffect(() => {
    // Extend tiptap's TextStyle to allow custom fontSize
    const FontSizeTextStyle = TextStyle.extend({
      addAttributes() {
        return {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        };
      },
    });

    // Example: Custom Bold extension to wrap text in a <span style="font-weight: bold">
    const CustomBold = Bold.extend({
      renderHTML({ mark, HTMLAttributes }) {
        const { style, ...rest } = HTMLAttributes;
        const newStyle = `font-weight: bold;${style ? ` ${style}` : ''}`;
        return ['span', { ...rest, style: newStyle.trim() }, 0];
      },
    });

    // Initialize the Tiptap Editor
    const editor = new Editor({
      element: editorContainerRef.current.querySelector('#wysiwyg-example'),
      extensions: [
        StarterKit.configure({
          textStyle: false,
          bold: false,
          marks: { bold: false },
        }),
        CustomBold,
        TextStyle,
        Color,
        FontSizeTextStyle,
        FontFamily,
        Highlight,
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Image,
        YouTube,
      ],
      content: `${HTMLInput}`,
      editorProps: {
        attributes: {
          class:
            'format lg:format-lg dark:format-invert focus:outline-none format-blue max-w-none',
        },
      },
      onUpdate({ editor }) {
        // Whenever the editor content changes, call onChange with the new HTML
        if (onChange) {
          onChange(editor.getHTML());
        }
      },
    });

    editorRef.current = editor;

    // --- Event Listeners for the Entire Toolbar ---

    // Bold
    const boldBtn = editorContainerRef.current.querySelector('#toggleBoldButton');
    if (boldBtn) {
      boldBtn.addEventListener('click', () => editor.chain().focus().toggleBold().run());
    }

    // Italic
    const italicBtn = editorContainerRef.current.querySelector('#toggleItalicButton');
    if (italicBtn) {
      italicBtn.addEventListener('click', () => editor.chain().focus().toggleItalic().run());
    }

    // Underline
    const underlineBtn = editorContainerRef.current.querySelector('#toggleUnderlineButton');
    if (underlineBtn) {
      underlineBtn.addEventListener('click', () => editor.chain().focus().toggleUnderline().run());
    }

    // Strike
    const strikeBtn = editorContainerRef.current.querySelector('#toggleStrikeButton');
    if (strikeBtn) {
      strikeBtn.addEventListener('click', () => editor.chain().focus().toggleStrike().run());
    }

    // Highlight
    const highlightBtn = editorContainerRef.current.querySelector('#toggleHighlightButton');
    if (highlightBtn) {
      highlightBtn.addEventListener('click', () => {
        const isHighlighted = editor.isActive('highlight');
        editor
          .chain()
          .focus()
          .toggleHighlight({ color: isHighlighted ? undefined : '#ffc078' })
          .run();
      });
    }

    // Code
    const codeBtn = editorContainerRef.current.querySelector('#toggleCodeButton');
    if (codeBtn) {
      codeBtn.addEventListener('click', () => editor.chain().focus().toggleCode().run());
    }

    // Link
    const linkBtn = editorContainerRef.current.querySelector('#toggleLinkButton');
    if (linkBtn) {
      linkBtn.addEventListener('click', () => {
        const url = window.prompt('Enter link URL:', 'https://flowbite.com');
        if (url) {
          editor.chain().focus().toggleLink({ href: url }).run();
        }
      });
    }

    // Remove Link
    const removeLinkBtn = editorContainerRef.current.querySelector('#removeLinkButton');
    if (removeLinkBtn) {
      removeLinkBtn.addEventListener('click', () => {
        editor.chain().focus().unsetLink().run();
      });
    }

    // Left Align
    const leftAlignBtn = editorContainerRef.current.querySelector('#toggleLeftAlignButton');
    if (leftAlignBtn) {
      leftAlignBtn.addEventListener('click', () => {
        editor.chain().focus().setTextAlign('left').run();
      });
    }

    // Center Align
    const centerAlignBtn = editorContainerRef.current.querySelector('#toggleCenterAlignButton');
    if (centerAlignBtn) {
      centerAlignBtn.addEventListener('click', () => {
        editor.chain().focus().setTextAlign('center').run();
      });
    }

    // Right Align
    const rightAlignBtn = editorContainerRef.current.querySelector('#toggleRightAlignButton');
    if (rightAlignBtn) {
      rightAlignBtn.addEventListener('click', () => {
        editor.chain().focus().setTextAlign('right').run();
      });
    }

    // Bullet List
    const listBtn = editorContainerRef.current.querySelector('#toggleListButton');
    if (listBtn) {
      listBtn.addEventListener('click', () => {
        editor.chain().focus().toggleBulletList().run();
      });
    }

    // Ordered List
    const orderedListBtn = editorContainerRef.current.querySelector('#toggleOrderedListButton');
    if (orderedListBtn) {
      orderedListBtn.addEventListener('click', () => {
        editor.chain().focus().toggleOrderedList().run();
      });
    }

    // Blockquote
    const blockquoteBtn = editorContainerRef.current.querySelector('#toggleBlockquoteButton');
    if (blockquoteBtn) {
      blockquoteBtn.addEventListener('click', () => {
        editor.chain().focus().toggleBlockquote().run();
      });
    }

    // Horizontal Rule
    const hrBtn = editorContainerRef.current.querySelector('#toggleHRButton');
    if (hrBtn) {
      hrBtn.addEventListener('click', () => {
        editor.chain().focus().setHorizontalRule().run();
      });
    }

    // Insert Image
    const imageBtn = editorContainerRef.current.querySelector('#addImageButton');
    if (imageBtn) {
      imageBtn.addEventListener('click', () => {
        const url = window.prompt('Enter image URL:', 'https://placehold.co/600x400');
        if (url) {
          editor.chain().focus().setImage({ src: url }).run();
        }
      });
    }

    // Insert YouTube Video
    const videoBtn = editorContainerRef.current.querySelector('#addVideoButton');
    if (videoBtn) {
      videoBtn.addEventListener('click', () => {
        const url = window.prompt('Enter YouTube URL:', 'https://www.youtube.com/watch?v=KaLxCiilHns');
        if (url) {
          editor.commands.setYoutubeVideo({
            src: url,
            width: 640,
            height: 480,
          });
        }
      });
    }

    // Paragraph button
    const paragraphBtn = editorContainerRef.current.querySelector('#toggleParagraphButton');
    if (paragraphBtn) {
      paragraphBtn.addEventListener('click', () => {
        editor.chain().focus().setParagraph().run();
      });
    }

    // Heading (1-6) buttons
    editorContainerRef.current.querySelectorAll('[data-heading-level]').forEach((button) => {
      button.addEventListener('click', () => {
        const level = button.getAttribute('data-heading-level');
        editor.chain().focus().toggleHeading({ level: parseInt(level, 10) }).run();
      });
    });

    // Text Size (12px, 14px, 16px, etc.)
    editorContainerRef.current.querySelectorAll('[data-text-size]').forEach((button) => {
      button.addEventListener('click', () => {
        const fontSize = button.getAttribute('data-text-size');
        editor.chain().focus().setMark('textStyle', { fontSize }).run();
      });
    });

    // Color Picker
    const colorPicker = editorContainerRef.current.querySelector('#color');
    if (colorPicker) {
      colorPicker.addEventListener('input', (event) => {
        const selectedColor = event.target.value;
        editor.chain().focus().setColor(selectedColor).run();
      });
    }

    // Color swatches
    editorContainerRef.current.querySelectorAll('[data-hex-color]').forEach((button) => {
      button.addEventListener('click', () => {
        const selectedColor = button.getAttribute('data-hex-color');
        editor.chain().focus().setColor(selectedColor).run();
      });
    });

    // Reset color
    const resetColorBtn = editorContainerRef.current.querySelector('#reset-color');
    if (resetColorBtn) {
      resetColorBtn.addEventListener('click', () => {
        editor.commands.unsetColor();
      });
    }

    // Font Family
    editorContainerRef.current.querySelectorAll('[data-font-family]').forEach((button) => {
      button.addEventListener('click', () => {
        const fontFamily = button.getAttribute('data-font-family');
        editor.chain().focus().setFontFamily(fontFamily).run();
      });
    });

    // ---- NEW: Undo and Redo buttons ----

    // Undo
    const undoBtn = editorContainerRef.current.querySelector('#toggleUndoButton');
    if (undoBtn) {
      undoBtn.addEventListener('click', () => editor.chain().focus().undo().run());
    }

    // Redo
    const redoBtn = editorContainerRef.current.querySelector('#toggleRedoButton');
    if (redoBtn) {
      redoBtn.addEventListener('click', () => editor.chain().focus().redo().run());
    }

    // Cleanup on unmount
    return () => {
      editor.destroy();
    };
  }, [onChange]);

  return (
    <div ref={editorContainerRef}>
      <div className="w-full border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
        {/* Toolbar */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
          <div className="flex flex-wrap items-center">
            <div className="flex items-center rtl:space-x-reverse flex-wrap">
              {/* BOLD */}
              <button
                id="toggleBoldButton"
                data-tooltip-target="tooltip-bold"
                type="button"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 
                  dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 5h4.5a3.5 3.5 0 1 1 0 7H8m0-7v7m0-7H6m2 7h6.5a3.5 3.5 0 1 1 0 7H8m0-7v7m0 0H6"
                  />
                </svg>
                <span className="sr-only">Bold</span>
              </button>
              <div
                id="tooltip-bold"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip 
                  dark:bg-gray-700"
              >
                Toggle bold
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* ITALIC */}
              <button
                id="toggleItalicButton"
                data-tooltip-target="tooltip-italic"
                type="button"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 
                  dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m8.874 19 6.143-14M6 19h6.33m-.66-14H18"
                  />
                </svg>
                <span className="sr-only">Italic</span>
              </button>
              <div
                id="tooltip-italic"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip 
                  dark:bg-gray-700"
              >
                Toggle italic
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* UNDERLINE */}
              <button
                id="toggleUnderlineButton"
                data-tooltip-target="tooltip-underline"
                type="button"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 
                  dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                    d="M6 19h12M8 5v9a4 4 0 0 0 8 0V5M6 5h4m4 0h4"
                  />
                </svg>
                <span className="sr-only">Underline</span>
              </button>
              <div
                id="tooltip-underline"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip 
                  dark:bg-gray-700"
              >
                Toggle underline
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* STRIKE */}
              <button
                id="toggleStrikeButton"
                data-tooltip-target="tooltip-strike"
                type="button"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 
                  dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 6.2V5h12v1.2M7 19h6m.2-14-1.677 6.523M9.6 19l1.029-4M5 5l6.523 6.523M19 19l-7.477-7.477"
                  />
                </svg>
                <span className="sr-only">Strike</span>
              </button>
              <div
                id="tooltip-strike"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip 
                  dark:bg-gray-700"
              >
                Toggle strike
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* HIGHLIGHT */}
              <button
                id="toggleHighlightButton"
                data-tooltip-target="tooltip-highlight"
                type="button"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 
                  dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                    d="M9 19.2H5.5c-.3 0-.5-.2-.5-.5V16c0-.2.2-.4.5-.4h13c.3 0 .5.2.5.4v2.7c0 .3-.2.5-.5.5H18m-6-1 1.4 1.8h.2l1.4-1.7m-7-5.4L12 4c0-.1 0-.1 0 0l4 8.8m-6-2.7h4m-7 2.7h2.5m5 0H17"
                  />
                </svg>
                <span className="sr-only">Highlight</span>
              </button>
              <div
                id="tooltip-highlight"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip 
                  dark:bg-gray-700"
              >
                Toggle highlight
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* CODE */}
              <button
                id="toggleCodeButton"
                type="button"
                data-tooltip-target="tooltip-code"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 
                  dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m8 8-4 4 4 4m8 0 4-4-4-4m-2-3-4 14"
                  />
                </svg>
                <span className="sr-only">Code</span>
              </button>
              <div
                id="tooltip-code"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip 
                  dark:bg-gray-700"
              >
                Format code
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* LINK */}
              <button
                id="toggleLinkButton"
                data-tooltip-target="tooltip-link"
                type="button"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 
                  dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13.213 9.787a3.391 3.391 0 0 0-4.795 0l-3.425 3.426a3.39 3.39 0 0 0 4.795 4.794l.321-.304m-.321-4.49a3.39 3.39 0 0 0 4.795 0l3.424-3.426a3.39 3.39 0 0 0-4.794-4.795l-1.028.961"
                  />
                </svg>
                <span className="sr-only">Link</span>
              </button>
              <div
                id="tooltip-link"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip 
                  dark:bg-gray-700"
              >
                Add link
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* REMOVE LINK */}
              <button
                id="removeLinkButton"
                data-tooltip-target="tooltip-remove-link"
                type="button"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 
                  dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                    d="M13.2 9.8a3.4 3.4 0 0 0-4.8 0L5 13.2A3.4 3.4 0 0 0 9.8 18l.3-.3m-.3-4.5a3.4 3.4 0 0 0 4.8 0L18 9.8A3.4 3.4 0 0 0 13.2 5l-1 1m7.4 14-1.8-1.8m0 0L16 16.4m1.8 1.8 1.8-1.8m-1.8 1.8L16 20"
                  />
                </svg>
                <span className="sr-only">Remove link</span>
              </button>
              <div
                id="tooltip-remove-link"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip 
                  dark:bg-gray-700"
              >
                Remove link
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* TEXT SIZE */}
              <button
                id="toggleTextSizeButton"
                data-dropdown-toggle="textSizeDropdown"
                type="button"
                data-tooltip-target="tooltip-text-size"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 
                  dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 6.2V5h11v1.2M8 5v14m-3 0h6m2-6.8V11h8v1.2M17 11v8m-1.5 0h3"
                  />
                </svg>
                <span className="sr-only">Text size</span>
              </button>
              <div
                id="tooltip-text-size"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip 
                  dark:bg-gray-700"
              >
                Text size
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* TEXT COLOR */}
              <button
                id="toggleTextColorButton"
                data-dropdown-toggle="textColorDropdown"
                type="button"
                data-tooltip-target="tooltip-text-color"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 
                  dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="24"
                  fill="none"
                  viewBox="0 0 25 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                    d="m6.532 15.982 1.573-4m-1.573 4h-1.1m1.1 0h1.65m-.077-4 2.725-6.93a.11.11 0 0 1 .204 0l2.725 6.93m-5.654 0H8.1m.006 0h5.654m0 0 .617 1.569m5.11 4.453c0 1.102-.854 1.996-1.908 1.996-1.053 0-1.907-.894-1.907-1.996 0-1.103 1.907-4.128 1.907-4.128s1.909 3.025 1.909 4.128Z"
                  />
                </svg>
                <span className="sr-only">Text color</span>
              </button>
              <div
                id="tooltip-text-color"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip 
                  dark:bg-gray-700"
              >
                Text color
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* FONT FAMILY */}
              <button
                id="toggleFontFamilyButton"
                data-dropdown-toggle="fontFamilyDropdown"
                type="button"
                data-tooltip-target="tooltip-font-family"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 
                  hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m10.6 19 4.298-10.93a.11.11 0 0 1 .204 0L19.4 19m-8.8 0H9.5m1.1 0h1.65m7.15 0h-1.65m1.65 0h1.1m-7.7-3.985h4.4M3.021 16l1.567-3.985m0 0L7.32 5.07a.11.11 0 0 1 .205 0l2.503 6.945h-5.44Z"
                  />
                </svg>
                <span className="sr-only">Font family</span>
              </button>
              <div
                id="tooltip-font-family"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium 
                  text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs 
                  opacity-0 tooltip dark:bg-gray-700"
              >
                Font Family
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* Divider */}
              <div className="px-1">
                <span className="block w-px h-4 bg-gray-300 dark:bg-gray-600" />
              </div>

              {/* LEFT ALIGN */}
              <button
                id="toggleLeftAlignButton"
                type="button"
                data-tooltip-target="tooltip-left-align"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 
                  hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                    d="M6 6h8m-8 4h12M6 14h8m-8 4h12"
                  />
                </svg>
                <span className="sr-only">Align left</span>
              </button>
              <div
                id="tooltip-left-align"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium 
                  text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs 
                  opacity-0 tooltip dark:bg-gray-700"
              >
                Align left
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* CENTER ALIGN */}
              <button
                id="toggleCenterAlignButton"
                type="button"
                data-tooltip-target="tooltip-center-align"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 
                  hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                    d="M8 6h8M6 10h12M8 14h8M6 18h12"
                  />
                </svg>
                <span className="sr-only">Align center</span>
              </button>
              <div
                id="tooltip-center-align"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium 
                  text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs 
                  opacity-0 tooltip dark:bg-gray-700"
              >
                Align center
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* RIGHT ALIGN */}
              <button
                id="toggleRightAlignButton"
                type="button"
                data-tooltip-target="tooltip-right-align"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 
                  hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                    d="M18 6h-8m8 4H6m12 4h-8m8 4H6"
                  />
                </svg>
                <span className="sr-only">Align right</span>
              </button>
              <div
                id="tooltip-right-align"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium 
                  text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs 
                  opacity-0 tooltip dark:bg-gray-700"
              >
                Align right
                <div className="tooltip-arrow" data-popper-arrow />
              </div>

              {/* ---- UNDO BUTTON ---- */}
              {/* <button
                id="toggleUndoButton"
                data-tooltip-target="tooltip-undo"
                type="button"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 9h13a5 5 0 0 1 0 10H7M3 9l4-4M3 9l4 4"
                  />
                </svg>
                <span className="sr-only">Undo</span>
              </button>
              <div
                id="tooltip-undo"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip dark:bg-gray-700"
              >
                Undo
                <div className="tooltip-arrow" data-popper-arrow />
              </div> */}

              {/* ---- REDO BUTTON ---- */}
              {/* <button
                id="toggleRedoButton"
                data-tooltip-target="tooltip-redo"
                type="button"
                className="p-1.5 text-gray-500 rounded-sm cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 9H8a5 5 0 0 0 0 10h9m4-10-4-4m4 4-4 4"
                  />
                </svg>
                <span className="sr-only">Redo</span>
              </button>
              <div
                id="tooltip-redo"
                role="tooltip"
                className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white 
                  transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip dark:bg-gray-700"
              >
                Redo
                <div className="tooltip-arrow" data-popper-arrow />
              </div> */}
            </div>
          </div>
        </div>

        {/* Editor content area */}
        <div className="px-3 py-2 bg-white rounded-b-lg dark:bg-gray-800">
          <label htmlFor="wysiwyg-example" className="sr-only">
            Publish post
          </label>
          <div
            id="wysiwyg-example"
            onClick={() => editorRef.current?.chain().focus().run()}
            style={{
              minHeight: minHeight,
              maxHeight: maxHeight,
              minWidth: minWidth,
              maxWidth: maxWidth,
              overflowY: maxHeight ? 'auto' : 'visible',
              cursor: 'text'
            }}
            className="block w-full px-0 text-sm text-gray-800 bg-white border-0 
              dark:bg-gray-800 focus:ring-0 dark:text-white dark:placeholder-gray-400"
          />
        </div>
      </div>
    </div>
  );
};

export default WysiwygEditor;
